import { safeCall } from '@xemplo/safe-call';
import { Instance, ModuleType } from '@xemplo/types';

const LOCAL_DNS = new Set(['localhost', '127.0.0.1']);

/**
 * Hosts fronted by the reverse proxy we run on a local machine to simulate a
 * live environment. It puts the individual dev servers on one origin, so apps
 * can be navigated between as the user would in production.
 *
 * One name per module — `local-hr-dev`, `local-hire-dev`,
 * `local-globalpayroll-dev` — mirroring the deployed split, so the pattern is
 * matched rather than a single host being named. The hosts themselves are
 * provisioned by `xfe setup`.
 */
const SIMULATED_LIVE_HOST_PATTERN = /^local-[a-z0-9-]+\.xemplo\.team$/;

/** True when the app is served straight off a developer's machine. */
export function isLocalHost() {
  return LOCAL_DNS.has(window.location.hostname);
}

/** True when `hostname` is one of the local reverse proxy's names. */
export function isSimulatedLiveHostname(hostname: string) {
  return SIMULATED_LIVE_HOST_PATTERN.test(hostname);
}

/** True when the app is served through the local reverse proxy. */
export function isSimulatedLiveHost() {
  return isSimulatedLiveHostname(window.location.hostname);
}

/**
 * True when this app is running inside a frame rather than as the top-level page.
 *
 * A framed app is a guest: the host chose the url it was given, owns the address bar,
 * and is the only one that can navigate the page. Anything that assumes otherwise -
 * resuming a stored route, assigning `location` - is wrong inside a frame.
 */
export function isFramed() {
  return window.parent !== window;
}

/**
 * The origin of the page framing this one, or null when it cannot be determined.
 *
 * `ancestorOrigins` is the direct answer where it exists (not Firefox); the referrer is
 * what a framed document has otherwise, and an empty one throws out of `new URL` into
 * the null. Null for a top-level page, which has no framer.
 */
export function getHostOrigin(): string | null {
  const [origin] = safeCall(
    () => window.location.ancestorOrigins?.[0] ?? new URL(document.referrer).origin,
  );

  return origin ?? null;
}

/**
 * True when `url` is served from this page's origin.
 *
 * Origin equality is what decides whether an in-app `navigate()` can reach a
 * destination at all: a pushState cannot change origin, so anything on another
 * origin needs a real page load. Malformed or absent urls are not this origin.
 */
export function isSameOrigin(url?: string | null) {
  const [origin] = safeCall(() => new URL(url ?? '').origin);

  return origin === window.location.origin;
}

/**
 * Resolves the URL of an instance's HR module, or undefined when the instance
 * is absent or exposes no HR module. HR-ness is determined solely by the
 * presence of an HR module — never inferred from a persona's defaultUrl, which
 * points at the persona's default landing app and may be a non-HR module.
 */
export function getHrModuleUrl(instance?: Instance | null): string | undefined {
  return instance?.modules?.find((module) => module.moduleType === ModuleType.HR)?.url;
}
